import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'baseui/modal';
import { Select, Value } from 'baseui/select';
import { BasicAuthEditor } from '@app/api-mgmt/components/push-api/components/basic-auth-editor/BasicAuthEditor';
import { NoAuthEditor } from '@app/api-mgmt/components/push-api/components/no-auth-editor/NoAuthEditor';
import { OAuthEditor } from '@app/api-mgmt/components/push-api/components/oauth-editor/OAuthEditor';
import { SfdcEditor } from '@app/api-mgmt/components/push-api/components/sfdc-editor/SfdcEditor';
import { SfdcAppExchangeEditor } from '@app/api-mgmt/components/push-api/components/sfdc-app-exchange-editor/SfdcAppExchangeEditor';
import {
  BasicAuthConfiguration,
  ModifiableIntegration,
  NoAuthConfiguration,
  OAuthConfiguration,
  SfdcAppExchangeConfiguration,
  SfdcConfiguration,
  SfmcConfiguration,
} from '@app/api-mgmt/components/push-api/types/push-api-models';
import {
  DropdownContainerWhiteOverride,
  ModalHeaderOverride,
  SelectFilterComponentsOverride,
  SelectIconsContainerOverride,
  SelectIconsOverride,
  SelectInputOverride,
  ButtonCancelOverride,
  ButtonOverride,
} from '@app/react-baseweb/baseweb-custom-theme/customTheme';
import { SfmcEditor } from '@app/api-mgmt/components/push-api/components/sfmc-editor/SfmcEditor';
import { Button } from 'baseui/button';

export const AddIntegrationModal = ({
  onIntegrationAddClicked,
  onCancelClicked,
}: {
  onIntegrationAddClicked: (newIntegration: ModifiableIntegration) => Promise<void>;
  onCancelClicked: () => void;
}) => {
  const [configTypeValue, setConfigTypeValue] = React.useState<Value>([]);
  const [currentStep, setCurrentStep] = React.useState(1);
  const [isConfirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    setCurrentStep(1);
  }, [configTypeValue]);

  const handleWasEdited = () => {
    setIsEdited(true);
  };
  
  const checkCloseSource = (e?) => {
    if (e?.closeSource && e?.closeSource === 'backdrop') return;
    if (isEdited) {
      setConfirmModalOpen(true);
    } else {
      onCancelClicked();
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={(e) => checkCloseSource(e)}
        overrides={{
          Dialog: {
            style: {
              marginTop: '75px',
              width: '45%',
              visibility: isConfirmModalOpen ? 'hidden' : 'visible',
            },
          },
        }}
      >
        <ModalHeader style={ModalHeaderOverride.style}>
          Add New Push API Integration
        </ModalHeader>
        <ModalBody>
          <Select
            overrides={{
              ControlContainer: SelectFilterComponentsOverride,
              ClearIcon: {
                props: { overrides: { Svg: SelectIconsOverride } },
              },
              SelectArrow: {
                props: { overrides: { Svg: SelectIconsOverride } },
              },
              IconsContainer: SelectIconsContainerOverride,
              Dropdown: DropdownContainerWhiteOverride,
              DropdownListItem: DropdownContainerWhiteOverride,
              ValueContainer: SelectInputOverride,
            }}
            options={[
              { label: 'Basic Authentication', id: 'basic-auth' },
              { label: 'Custom', id: 'no-auth' },
              { label: 'OAuth', id: 'oauth' },
              {
                label: 'Salesforce App Exchange Package',
                id: 'sfdc-app-exchange',
              },
              { label: 'Salesforce Marketing Cloud', id: 'sfmc' },
              { label: 'Salesforce Unmanaged Package', id: 'sfdc' },
            ]}
            value={configTypeValue}
            placeholder="Select Authentication Type..."
            onChange={({ value }) => setConfigTypeValue(value)}
          />

          {configTypeValue[0]?.id === 'basic-auth' && (
            <BasicAuthEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              onWasEdited={handleWasEdited}
              integration={{} as BasicAuthConfiguration}
            />
          )}

          {configTypeValue[0]?.id === 'no-auth' && (
            <NoAuthEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              onWasEdited={handleWasEdited}
              integration={{} as NoAuthConfiguration}
            />
          )}

          {configTypeValue[0]?.id === 'oauth' && (
            <OAuthEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              onWasEdited={handleWasEdited}
              integration={{} as OAuthConfiguration}
            />
          )}

          {configTypeValue[0]?.id === 'sfdc' && (
            <SfdcEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              integration={{} as SfdcConfiguration}
            />
          )}

          {configTypeValue[0]?.id === 'sfdc-app-exchange' && (
            <SfdcAppExchangeEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              integration={{} as SfdcAppExchangeConfiguration}
            />
          )}

          {configTypeValue[0]?.id === 'sfmc' && (
            <SfmcEditor
              onSaveClicked={onIntegrationAddClicked}
              onCancelClicked={onCancelClicked}
              integration={{} as SfmcConfiguration}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        overrides={{
          Dialog: {
            style: {
              marginTop: '75px',
              width: '35%',
            },
          },
        }}
      >
        <ModalHeader style={ModalHeaderOverride.style}>
          Are you sure you want to exit this Push API Integration setup?
        </ModalHeader>
        <ModalBody>
          Please complete all required fields to save your configuration. If you
          exit without saving you will need to start over again.{' '}
        </ModalBody>
        <ModalFooter>
          <div style={{ float: 'right', paddingBottom: '20px' }}>
            <Button
              onClick={onCancelClicked}
              overrides={{
                Root: {
                  ...ButtonCancelOverride,
                  style: {
                    ...ButtonCancelOverride.style,
                    backgroundColor: 'white',
                  },
                },
              }}
              kind="secondary"
            >
              Exit without saving
            </Button>
            <Button
              onClick={() => setConfirmModalOpen(false)}
              overrides={{
                BaseButton: {
                  ...ButtonOverride,
                  style: {
                    ...ButtonOverride.style,
                    height: '42px',
                    paddingLeft: '15px',
                    paddingRight: '15px',
                  },
                },
              }}
            >
              CONTINUE EDITING
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};
